import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { SlidersHorizontal, ShieldCheck, MapPin, CalendarDays, ChevronDown, ChevronUp, Layers, Clock, Building2, CheckCircle2, TrendingUp, Maximize2, Minimize2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { PageHeader, StatusBadge, formatDate, categoryLabel, STATUS_LABELS, useIssueCategories } from '../components/Shared';
import { WardOverlays, WardSourceAttribution, FitBounds, wardBounds } from '../components/WardMap';
import { ShareButtons, PinBadges } from '../components/TrackerShared';
import { pushpinIcon, LegendPushpin, STAGE_PIN_COLORS, STAGE_LEGEND } from '../components/MapPushpin';
import { usePitaLogo } from '../components/PitaCTA';

// (Phase 97) Pin colours now come from STAGE_PIN_COLORS in components/MapPushpin.jsx
// Distinct colours for the category pattern view
const CATEGORY_COLORS = ['#3E6FA3', '#4E8B50', '#B07C2B', '#8E4E86', '#A34747', '#3F8683', '#6C5AA8', '#8a6f22', '#475569', '#B54708'];

const RESOLVED_SET = new Set(['resolved', 'closed']);

const VIEW_MODES = [
  { value: 'ward8', label: 'Ward 8' },
  { value: 'all_wards', label: 'All Wards' },
  { value: 'issues', label: 'Issues' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'patterns', label: 'Patterns' },
];

// Public tracker stage filters (privacy-safe groupings)
const STAGE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'awaiting_city_response', label: 'Awaiting City Response' },
  { value: 'resolved', label: 'Resolved' },
];

const STAGE_LABELS = {
  open: 'New / Open',
  in_progress: 'In Progress',
  awaiting_city_response: 'Awaiting City Response',
  resolved: 'Resolved',
};

const WARD_FILTERS = ['all', '8', '1', '2', '3', '4', '5', '6', '7'];

const DATE_RANGES = [
  { value: 'all', label: 'Any time' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'This year' },
];

const monthLabel = (ym) => {
  const [y, m] = (ym || '').split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m, 10) - 1] || m} ${(y || '').slice(2)}`;
};

// Reports vs. Resolutions — privacy-safe monthly trend chart
const TrendChart = ({ trends }) => {
  const data = useMemo(() => {
    if (!trends?.months) return [];
    return trends.months.map((m, i) => ({
      month: monthLabel(m),
      Submitted: trends.submitted?.[i] ?? 0,
      Resolved: trends.resolved?.[i] ?? 0,
      'Still open': trends.still_open?.[i] ?? 0,
      'Avg days open': trends.avg_days_open?.[i] ?? 0,
    }));
  }, [trends]);
  if (!data.length) return null;
  return (
    <Card className="mb-5" data-testid="tracker-trend-chart">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start gap-2">
          <TrendingUp className="h-5 w-5 text-[#C9A84C] mt-1 shrink-0" />
          <div>
            <h2 className="font-serif font-black text-[#1a2744] text-lg sm:text-xl leading-tight" data-testid="trend-chart-title">Reports vs. Resolutions</h2>
            <p className="text-sm text-slate-600 mt-0.5" data-testid="trend-chart-subtitle">See whether issues are being reported, followed up, and resolved over time.</p>
          </div>
        </div>
        <div className="h-56 sm:h-64 mt-4 -ml-2" data-testid="trend-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="days" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={30} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Submitted" fill="#1a2744" radius={[3, 3, 0, 0]} maxBarSize={26} />
              <Bar dataKey="Resolved" fill="#067647" radius={[3, 3, 0, 0]} maxBarSize={26} />
              <Line type="monotone" dataKey="Still open" stroke="#B54708" strokeWidth={2} dot={{ r: 2 }} />
              <Line yAxisId="days" type="monotone" dataKey="Avg days open" stroke="#C9A84C" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Based only on public reports. Privacy-safe aggregates: no names, no addresses.</p>
      </CardContent>
    </Card>
  );
};

// Small privacy-safe badges shown on popups + list cards
// (PinBadges is imported from ../components/TrackerShared)

const Filters = ({ category, setCategory, stage, setStage, community, setCommunity, range, setRange, ward, setWard, communities }) => {  const liveCategories = useIssueCategories();
  return (
  <div className="space-y-4" data-testid="public-map-filters">
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Status</p>
      <Select value={stage} onValueChange={setStage}>
        <SelectTrigger className="h-11 bg-white" data-testid="map-filter-status"><SelectValue /></SelectTrigger>
        <SelectContent>
          {STAGE_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.value === 'all' ? 'All statuses' : s.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Ward</p>
      <Select value={ward} onValueChange={setWard}>
        <SelectTrigger className="h-11 bg-white" data-testid="map-filter-ward"><SelectValue /></SelectTrigger>
        <SelectContent>
          {WARD_FILTERS.map((w) => <SelectItem key={w} value={w}>{w === 'all' ? 'All wards' : `Ward ${w}`}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Category</p>
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger className="h-11 bg-white" data-testid="map-filter-category"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All categories</SelectItem>
          {Object.entries(liveCategories).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Community</p>
      <Select value={community} onValueChange={setCommunity}>
        <SelectTrigger className="h-11 bg-white" data-testid="map-filter-community"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All communities</SelectItem>
          {communities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Date range</p>
      <Select value={range} onValueChange={setRange}>
        <SelectTrigger className="h-11 bg-white" data-testid="map-filter-range"><SelectValue /></SelectTrigger>
        <SelectContent>
          {DATE_RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
  );
};

// Captures the live Leaflet map instance so the fullscreen toggle can call
// invalidateSize() and keep every pin exactly anchored after resizing.
const MapRefCapture = ({ mapRef }) => {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
};

export default function PublicMap() {
  const pitaLogo = usePitaLogo('pita_page');
  const [searchParams] = useSearchParams();
  const [pins, setPins] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [wards, setWards] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [stage, setStage] = useState('all');
  const [community, setCommunity] = useState('all');
  const [wardFilter, setWardFilter] = useState('all');
  const [range, setRange] = useState('all');
  const [drawer, setDrawer] = useState(false);
  const [mode, setMode] = useState(searchParams.get('ward') && searchParams.get('ward') !== '8' ? 'all_wards' : 'ward8');
  const [showWards, setShowWards] = useState(true);
  const [legendOpen, setLegendOpen] = useState(false);

  // ---- Fullscreen map (native Fullscreen API + full-window fallback) ----
  const mapShellRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Phones: fullscreen filter chips start collapsed so the map is not buried.
  const [fsFiltersOpen, setFsFiltersOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 640 : true));
  const [fsFallback, setFsFallback] = useState(false);
  const [fsLegendOpen, setFsLegendOpen] = useState(false);

  // Re-measure the map after any fullscreen change so tiles render and every
  // pin stays exactly on its anchored coordinates (no drift on resize).
  const refreshMapSize = useCallback(() => {
    setTimeout(() => { try { leafletMapRef.current?.invalidateSize(); } catch { /* map not ready */ } }, 320);
  }, []);

  const enterFullscreen = useCallback(async () => {
    const el = mapShellRef.current;
    if (!el) return;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (request) {
      try { await request.call(el); return; } catch { /* blocked -> fallback */ }
    }
    // Fallback (e.g. iPhone Safari): expand to a fixed full-window overlay.
    setFsFallback(true);
    setIsFullscreen(true);
    refreshMapSize();
  }, [refreshMapSize]);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      setFsFallback(false);
      setIsFullscreen(false);
      setFsLegendOpen(false);
      refreshMapSize();
    }
  }, [refreshMapSize]);

  // Track native fullscreen state (also covers Escape, which the browser
  // handles natively in fullscreen mode).
  useEffect(() => {
    const onChange = () => {
      const active = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(active || fsFallback);
      if (!active && !fsFallback) setFsLegendOpen(false);
      refreshMapSize();
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [fsFallback, refreshMapSize]);

  // Escape support: native fullscreen exits via the browser itself, but we
  // also listen ourselves (covers the fallback mode and edge cases).
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') exitFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, exitFullscreen]);

  // Scroll lock for the full-window fallback mode.
  useEffect(() => {
    if (!fsFallback) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [fsFallback]);

  useEffect(() => {
    api.get('/map/pins').then((r) => setPins(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/map/summary').then((r) => setSummary(r.data || null)).catch(() => {});
    api.get('/map/trends').then((r) => setTrends(r.data || null)).catch(() => {});
    api.get('/communities').then((r) => setCommunities(r.data?.communities || [])).catch(() => {});
    api.get('/wards').then((r) => setWards(r.data?.wards || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const cutoff = range === 'all' ? null : new Date(Date.now() - parseInt(range, 10) * 86400000).toISOString().slice(0, 10);
    return (Array.isArray(pins) ? pins : []).filter((p) =>
      p &&
      (category === 'all' || p.category === category) &&
      (stage === 'all' || p.stage === stage) &&
      (wardFilter === 'all' || String(p.ward || '') === wardFilter) &&
      (community === 'all' || (p.community || '').toLowerCase() === community.toLowerCase()) &&
      (!cutoff || (p.date_reported || '') >= cutoff) &&
      (mode !== 'resolved' || RESOLVED_SET.has(p.status)) &&
      (mode !== 'issues' || !RESOLVED_SET.has(p.status))
    );
  }, [pins, category, stage, community, range, mode, wardFilter]);

  // Only pins with valid numeric coordinates can be drawn on the map
  const mappable = useMemo(() => filtered.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)), [filtered]);

  const categoryColorMap = useMemo(() => {
    const cats = [...new Set((pins || []).map((p) => p?.category).filter(Boolean))];
    const map = {};
    cats.forEach((c, i) => { map[c] = CATEGORY_COLORS[i % CATEGORY_COLORS.length]; });
    return map;
  }, [pins]);

  const pinColor = (p) => {
    if (mode === 'patterns') return categoryColorMap[p.category] || '#64748b';
    // Status colours (locked): Red=New/Open, Orange=In Progress,
    // Blue=Awaiting City Response, Green=Resolved. Driven by the report's
    // live stage, so the pin colour changes automatically with status.
    return STAGE_PIN_COLORS[p.stage] || (RESOLVED_SET.has(p.status) ? STAGE_PIN_COLORS.resolved : STAGE_PIN_COLORS.open);
  };

  const focusWard = mode === 'all_wards' ? null : 8;
  const bounds = useMemo(() => wardBounds(wards, focusWard) || wardBounds(wards, null), [wards, focusWard]);

  const activeFilters = [category, stage, community, range].filter((v) => v !== 'all').length;

  const legendBody = (
    <div>
      {mode === 'patterns' ? (
        Object.entries(categoryColorMap).length === 0 ? (
          <p className="text-sm text-slate-500 py-1">No categories to show yet.</p>
        ) : (
          Object.entries(categoryColorMap).map(([c, color]) => (
            <p key={c} className="flex items-center gap-2 text-sm text-slate-700 py-1">
              <LegendPushpin color={color} /> {categoryLabel(c)}
            </p>
          ))
        )
      ) : (
        STAGE_LEGEND.map(({ stage: v, label, color }) => (
          <p key={v} className="flex items-center gap-2 text-sm text-slate-700 py-1" data-testid={`legend-status-${v}`}>
            <LegendPushpin color={color} /> {label}
          </p>
        ))
      )}
      {showWards && wards.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Ward overlays</p>
          {(mode === 'all_wards' ? wards : wards.filter((w) => w.ward_number === 8)).map((w) => (
            <p key={w.ward_number} className="flex items-center gap-2 text-sm text-slate-700 py-0.5">
              <span className="inline-block h-3 w-3 rounded-sm border" style={{ background: w.overlay_color, borderColor: w.border_color }} /> {w.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader pageKey="public-map" kicker="PUBLIC ISSUE TRACKER APP (PITA)" title="Ward 8 Public Issue Map" subtitle="Report it. Map it. Track it. Get answers." logo={pitaLogo?.url} logoAlt={pitaLogo?.alt} />

      {/* Main line band */}
      <div className="bg-[#C9A84C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="font-serif font-bold text-[#1a2744] text-sm sm:text-base leading-snug" data-testid="tracker-main-line">
            One complaint can be overlooked. A map full of complaints cannot.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 overflow-x-hidden">
        {/* Privacy-safe summary bar — public/published records only */}
        {summary && (
          <Card className="mb-5" data-testid="tracker-summary-bar">
            <CardContent className="p-4 sm:p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="flex items-start gap-2" data-testid="summary-total-tracked">
                  <MapPin className="h-5 w-5 text-[#C9A84C] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xl sm:text-2xl font-black text-[#1a2744] leading-none">{summary.total_tracked}</p>
                    <p className="text-xs font-semibold text-slate-500 mt-1">issue{summary.total_tracked === 1 ? '' : 's'} tracked</p>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-testid="summary-resolved">
                  <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xl sm:text-2xl font-black text-[#1a2744] leading-none">{summary.resolved_count}</p>
                    <p className="text-xs font-semibold text-slate-500 mt-1">resolved</p>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-testid="summary-awaiting-city">
                  <Building2 className="h-5 w-5 text-violet-700 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xl sm:text-2xl font-black text-[#1a2744] leading-none">{summary.awaiting_city_response_count}</p>
                    <p className="text-xs font-semibold text-slate-500 mt-1">awaiting City response</p>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-testid="summary-avg-days">
                  <Clock className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xl sm:text-2xl font-black text-[#1a2744] leading-none">{summary.avg_days_open}</p>
                    <p className="text-xs font-semibold text-slate-500 mt-1">average days open</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick status filter chips — thumb-friendly */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="tracker-stage-chips">
          {STAGE_FILTERS.map((s) => (
            <Button
              key={s.value}
              onClick={() => setStage(s.value)}
              variant={stage === s.value ? 'default' : 'outline'}
              size="sm"
              className={`h-10 text-xs sm:text-sm font-bold btn-press ${stage === s.value ? 'bg-[#1a2744] hover:bg-[#243456] text-white' : 'border-slate-300 text-[#1a2744]'}`}
              data-testid={`stage-chip-${s.value}`}
            >
              {s.label}
            </Button>
          ))}
        </div>

        {/* View mode buttons — big touch targets for mobile */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-4" data-testid="map-mode-buttons">
          {VIEW_MODES.map((m) => (
            <Button
              key={m.value}
              onClick={() => setMode(m.value)}
              variant={mode === m.value ? 'default' : 'outline'}
              className={`h-12 font-bold btn-press ${mode === m.value ? 'bg-[#1a2744] hover:bg-[#243456] text-white' : 'border-slate-300 text-[#1a2744]'}`}
              data-testid={`map-mode-${m.value}`}
            >
              {m.label}
            </Button>
          ))}
          <label htmlFor="map-ward-overlay-switch" className="col-span-2 sm:col-span-1 sm:ml-auto inline-flex items-center gap-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg px-3 h-12 cursor-pointer" data-testid="map-ward-overlay-toggle-wrap">
            <Layers className="h-4 w-4 text-[#C9A84C]" /> Ward overlays
            <Switch id="map-ward-overlay-switch" checked={showWards} onCheckedChange={setShowWards} data-testid="map-ward-overlay-toggle" />
          </label>
        </div>

        {/* Public-safe badge + mobile filter trigger */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5" data-testid="map-public-safe-badge">
            <ShieldCheck className="h-3.5 w-3.5" /> Public-safe data: no names, no exact addresses
          </span>
          <div className="lg:hidden">
            <Sheet open={drawer} onOpenChange={setDrawer}>
              <SheetTrigger asChild>
                <Button variant="outline" className="h-11 font-bold btn-press" data-testid="map-filters-button">
                  <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Filters{activeFilters > 0 ? ` (${activeFilters})` : ''}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto" data-testid="map-filters-drawer">
                <p className="font-serif font-bold text-[#1a2744] text-lg mb-4">Filter the map</p>
                <Filters {...{ category, setCategory, stage, setStage, community, setCommunity, range, setRange, communities, ward: wardFilter, setWard: setWardFilter }} />
                <Button onClick={() => setDrawer(false)} className="mt-5 h-12 w-full bg-[#1a2744] text-white font-bold btn-press" data-testid="map-filters-apply">Show {filtered.length} report(s)</Button>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Desktop filters */}
          <div className="hidden lg:block lg:col-span-3">
            <Card><CardContent className="p-4">
              <p className="font-serif font-bold text-[#1a2744] mb-3">Filters</p>
              <Filters {...{ category, setCategory, stage, setStage, community, setCommunity, range, setRange, communities, ward: wardFilter, setWard: setWardFilter }} />
            </CardContent></Card>
            {/* Status Legend (collapsible) */}
            <Card className="mt-4"><CardContent className="p-4" data-testid="map-legend">
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="flex items-center justify-between w-full" data-testid="map-legend-toggle">
                  <p className="font-serif font-bold text-[#1a2744]">Status Legend</p>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </CollapsibleTrigger>
                <CollapsibleContent>{legendBody}</CollapsibleContent>
              </Collapsible>
            </CardContent></Card>
          </div>

          <div className="lg:col-span-9">
            <Card className="overflow-hidden">
              <div
                ref={mapShellRef}
                className={`relative bg-white ${fsFallback ? 'map-fs-fallback' : ''}`}
                data-testid="map-shell"
              >
                {/* Full Screen button (upper-right corner of the map) */}
                {!isFullscreen && (
                  <Button
                    onClick={enterFullscreen}
                    size="sm"
                    variant="outline"
                    className="absolute top-3 right-3 z-[1000] h-10 bg-white/95 hover:bg-white text-[#1a2744] font-bold shadow-md border-slate-300 btn-press"
                    data-testid="map-fullscreen-button"
                    aria-label="View the map full screen"
                  >
                    <Maximize2 className="h-4 w-4 mr-1.5" /> Full Screen
                  </Button>
                )}

                {/* Fullscreen controls: rendered INLINE (no portals) so they stay
                    visible inside the native-fullscreen element. */}
                {isFullscreen && (
                  <div className="absolute top-0 inset-x-0 z-[1001] p-3 pointer-events-none" data-testid="map-fullscreen-bar">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <span className="pointer-events-auto hidden sm:inline-block bg-white/95 border border-slate-200 shadow-md rounded-lg px-3 py-2 font-serif font-bold text-sm text-[#1a2744]">
                        Ward 8 Public Issue Map
                      </span>
                      <div className="flex gap-2 pointer-events-auto">
                        <Button
                          onClick={() => setFsFiltersOpen((v) => !v)}
                          size="sm"
                          variant="outline"
                          className="h-10 sm:hidden bg-white/95 hover:bg-white text-[#1a2744] font-bold shadow-md border-slate-300 btn-press"
                          data-testid="map-fullscreen-filters-toggle"
                          aria-expanded={fsFiltersOpen}
                        >
                          <SlidersHorizontal className="h-4 w-4 mr-1" /> Filters
                        </Button>
                        <Button
                          onClick={() => setFsLegendOpen((v) => !v)}
                          size="sm"
                          variant="outline"
                          className="h-10 bg-white/95 hover:bg-white text-[#1a2744] font-bold shadow-md border-slate-300 btn-press"
                          data-testid="map-fullscreen-legend-toggle"
                        >
                          Legend {fsLegendOpen ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                        </Button>
                        <Button
                          onClick={exitFullscreen}
                          size="sm"
                          className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold shadow-md btn-press"
                          data-testid="map-exit-fullscreen-button"
                          aria-label="Exit full screen (or press Escape)"
                        >
                          <Minimize2 className="h-4 w-4 mr-1.5" /> Exit Full Screen
                        </Button>
                      </div>
                    </div>
                    {/* Status filter chips (usable in fullscreen; collapsible on phones) */}
                    <div className={`${fsFiltersOpen ? 'flex' : 'hidden'} sm:flex flex-wrap gap-1.5 mt-2 pointer-events-auto`} data-testid="map-fullscreen-status-chips">
                      {STAGE_FILTERS.map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setStage(s.value)}
                          className={`text-xs font-bold rounded-full px-3 py-2 sm:py-1.5 min-h-10 sm:min-h-0 border shadow-sm transition-colors duration-150 ${stage === s.value ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white/95 text-[#1a2744] border-slate-300 hover:bg-white'}`}
                          data-testid={`map-fullscreen-stage-${s.value}`}
                        >
                          {s.value === 'all' ? 'All statuses' : s.label}
                        </button>
                      ))}
                    </div>
                    {/* Ward filter chips */}
                    <div className={`${fsFiltersOpen ? 'flex' : 'hidden'} sm:flex flex-wrap gap-1.5 mt-1.5 pointer-events-auto`} data-testid="map-fullscreen-ward-chips">
                      {WARD_FILTERS.map((w) => (
                        <button
                          key={w}
                          type="button"
                          onClick={() => setWardFilter(w)}
                          className={`text-xs font-bold rounded-full px-3 py-2 sm:py-1.5 min-h-10 sm:min-h-0 border shadow-sm transition-colors duration-150 ${wardFilter === w ? 'bg-[#C9A84C] text-[#1a2744] border-[#C9A84C]' : 'bg-white/95 text-[#1a2744] border-slate-300 hover:bg-white'}`}
                          data-testid={`map-fullscreen-ward-${w}`}
                        >
                          {w === 'all' ? 'All wards' : `W${w}`}
                        </button>
                      ))}
                    </div>
                    {fsLegendOpen && (
                      <div className="mt-2 pointer-events-auto max-w-xs bg-white/95 border border-slate-200 shadow-lg rounded-xl p-3 max-h-[50vh] overflow-y-auto" data-testid="map-fullscreen-legend">
                        <p className="font-serif font-bold text-[#1a2744] text-sm mb-1">Status Legend</p>
                        {legendBody}
                      </div>
                    )}
                  </div>
                )}

                <div className={isFullscreen ? 'h-full w-full' : 'h-[60vh] min-h-[380px] sm:h-[520px] w-full'} data-testid="public-map">
                  <MapContainer center={[44.36, -78.54]} zoom={10} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                    <MapRefCapture mapRef={leafletMapRef} />
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {bounds && <FitBounds bounds={bounds} />}
                    {showWards && <WardOverlays wards={wards} focusWard={focusWard} showLabels interactive />}
                    {mappable.map((p) => (
                      <Marker key={`${p.id}-${mode}`} position={[p.lat, p.lng]} icon={pushpinIcon(pinColor(p))}>
                        <Popup maxWidth={280}>
                          <div className="text-sm" data-testid="map-popup">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{categoryLabel(p.category)}</p>
                            <p className="font-bold leading-snug">{p.title}</p>
                            {p.public_summary && <p className="text-xs text-slate-600 mt-1 leading-relaxed">{p.public_summary}</p>}
                            <PinBadges pin={p} compact />
                            <p className="text-xs text-slate-500 mt-1.5">
                              {p.ward && <><span data-testid="popup-ward">Ward {p.ward}</span><br /></>}
                              {p.area && <>Area: {p.area}<br /></>}
                              Reported {formatDate(p.date_reported)}<br />
                              <span data-testid="popup-status">Status: {STAGE_LABELS[p.stage] || STATUS_LABELS[p.status] || p.status}</span>
                            </p>
                            <Link to={`/issue/${p.tracking_number}`} className="text-xs font-bold underline" data-testid="popup-view-details-link">View public report</Link>
                            <ShareButtons trackingNumber={p.tracking_number} title={p.title} compact className="mt-2" />
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </div>
            </Card>
            <WardSourceAttribution className="mt-2" />

            {/* Mobile legend (collapsible) */}
            <div className="lg:hidden mt-3" data-testid="map-legend-mobile">
              <Collapsible open={legendOpen} onOpenChange={setLegendOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="h-11 w-full justify-between font-bold btn-press" data-testid="map-legend-mobile-toggle">
                    Status legend {legendOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <Card className="mt-2"><CardContent className="p-4">{legendBody}</CardContent></Card>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Accessible list fallback */}
            <div className="mt-6">
              <p className="font-bold text-[#1a2744] mb-3">Mapped reports ({filtered.length})</p>
              {filtered.length === 0 ? (
                <Card><CardContent className="p-5 text-sm text-slate-500">No mapped reports match these filters. Public reports appear here with generalized locations.</CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filtered.map((p) => (
                    <Link key={p.id} to={`/issue/${p.tracking_number}`} data-testid="map-list-item">
                      <Card className="card-lift h-full"><CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs font-bold text-[#C9A84C]">{p.tracking_number}</span>
                          <StatusBadge status={p.status} />
                        </div>
                        <p className="font-semibold text-[#1a2744] text-sm mt-1.5 line-clamp-1">{p.title}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-slate-500">
                          <span>{categoryLabel(p.category)}</span>
                          {p.area && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.area}</span>}
                          <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {formatDate(p.date_reported)}</span>
                        </div>
                        <PinBadges pin={p} compact />
                      </CardContent></Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6">
              <TrendChart trends={trends} />
            </div>

            <div className="mt-2 text-center">
              <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="map-report-button"><Link to="/issue-tracker/report">Report an Issue</Link></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
